// Invoice list + lifecycle actions for the Billing Settlement panel.
//
// Which buttons a viewer sees comes from the workflow definition's authority rules (served by
// GET .../invoices/permissions), never from a role name here: Owner/Office Admin see amounts and
// can prepare, send and record payments; only the Owner reviews (approve/return); every other role
// (e.g. the Project Manager) gets a status-only list with amounts shown as "Restricted".
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localDate } from '@/utils/isoLocalDateTime';
import type { TFunction } from 'i18next';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Input } from '@/components/UI/Input';
import { Modal } from '@/components/UI/Modal';
import { Select } from '@/components/UI/Select';
import { Textarea } from '@/components/UI/Textarea';
import { subscribeEngagementSignal } from '@/components/SAM/samSignals';
import { useAppSelector } from '@/hooks';
import { actorLabel } from '@/utils/actorLabel';
import {
  useGetInvoicePermissionsQuery, useInvoiceActionMutation,
} from '@/redux/internal-services/jobsApi';
import type { Invoice, InvoiceActionCode, InvoiceStatus } from '@/redux/internal-services/jobsApi';

type BadgeTone = 'accent' | 'primary' | 'success' | 'warning' | 'danger' | 'steel' | 'neutral';

const STATUS_TONE: Record<InvoiceStatus, BadgeTone> = {
  Prepared: 'steel',
  Approved: 'accent',
  Sent: 'steel',
  'Partially Paid': 'warning',
  Paid: 'success',
  Returned: 'warning',
  Cancelled: 'danger',
};

const money = (n: number | undefined) => `$${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const day = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

interface RowAction {
  code: InvoiceActionCode;
  label: string;
  variant: 'accent' | 'outline' | 'destructive-ghost';
  /** reason = prompt for text first; payment = open the payment form; none = fire straight away */
  prompt: 'none' | 'reason' | 'payment' | 'resolve';
}

/** Actions available for an invoice, filtered by status AND by what the viewer's role may do. */
function actionsFor(inv: Invoice, can: Record<string, boolean>, t: TFunction): RowAction[] {
  const out: RowAction[] = [];
  // a payment reversed in QuickBooks no longer counts against the invoice
  const hasPayments = (inv.payments ?? []).some((p) => !p.reversed) || (inv.paid ?? 0) > 0;
  switch (inv.status) {
    case 'Prepared':
      if (can.invoice_review) {
        out.push({ code: 'approve', label: t('billing.action.approve', 'Approve'), variant: 'accent', prompt: 'none' });
        out.push({ code: 'return', label: t('billing.action.return', 'Return'), variant: 'outline', prompt: 'reason' });
      }
      break;
    case 'Returned':
      if (can.invoice_prepare) out.push({ code: 'resubmit', label: t('billing.action.resubmit', 'Resubmit'), variant: 'accent', prompt: 'none' });
      break;
    case 'Approved':
      if (can.invoice_send) out.push({ code: 'send', label: t('billing.action.send', 'Send'), variant: 'accent', prompt: 'none' });
      break;
    case 'Sent':
    case 'Partially Paid':
      if (can.invoice_record_payment) out.push({ code: 'record_payment', label: t('billing.action.record_payment', 'Record payment'), variant: 'accent', prompt: 'payment' });
      if (can.invoice_send) out.push({ code: 'resend', label: t('billing.action.resend', 'Resend'), variant: 'outline', prompt: 'none' });
      break;
    default:
      break;
  }
  // QuickBooks mirror: a part that failed / is held / suspended can be retried, and an open
  // reconciliation must be resolved by a person (automatic sync stays off until then).
  const rec = inv.quickbooks?.reconciliation;
  const openRec = !!rec && !rec.resolved;
  const qbRetryable = ['failed', 'held_for_review', 'suspended'].some((s) => inv.quickbooks?.invoice?.status === s || inv.quickbooks?.payments?.status === s);
  if (can.invoice_record_payment && openRec) {
    out.push({ code: 'resolve_quickbooks', label: t('billing.action.resolve_quickbooks', 'Resolve QuickBooks difference'), variant: 'accent', prompt: 'resolve' });
  } else if (can.invoice_record_payment && qbRetryable && ['Approved', 'Sent', 'Partially Paid', 'Paid'].includes(inv.status)) {
    out.push({ code: 'retry_quickbooks_sync', label: t('billing.action.retry_quickbooks_sync', 'Retry QuickBooks sync'), variant: 'outline', prompt: 'none' });
  }
  // Cancellation is allowed until ANY payment is recorded against the invoice.
  if (can.invoice_cancel && !hasPayments && ['Prepared', 'Returned', 'Approved', 'Sent'].includes(inv.status)) {
    out.push({ code: 'cancel', label: t('billing.action.cancel', 'Cancel'), variant: 'destructive-ghost', prompt: 'reason' });
  }
  return out;
}

const QB_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending sync',
  synced: 'Synced',
  failed: 'Failed',
  held_for_review: 'Held for review',
  reconciliation_required: 'Reconciliation required',
  suspended: 'Suspended — reconnect QuickBooks',
};

/** The mockup's QUICKBOOKS block: invoice / payment sync with the platform status vocabulary, the cause
 *  and retry progress, an open reconciliation (with what QuickBooks changed), and — after Billing
 *  Settled — the changes seen in QuickBooks that were only recorded. */
const QuickBooksBlock: React.FC<{ inv: Invoice }> = ({ inv }) => {
  const { t } = useTranslation();
  const q = inv.quickbooks;
  if (!q) return null;
  const label = (s?: string) => t(`billing.qbStatus.${s}`, QB_STATUS_LABEL[s ?? 'pending'] ?? s ?? '—');
  const tone = (s?: string) => (s === 'synced' ? 'text-ui-text' : s === 'pending' || !s ? 'text-ui-text2' : 'text-danger-text');
  const rec = q.reconciliation && !q.reconciliation.resolved ? q.reconciliation : null;
  const part = (p?: NonNullable<Invoice['quickbooks']>['invoice']) => {
    if (!p) return '—';
    const base = p.status === 'synced'
      ? `${label(p.status)}${p.doc_number || p.qb_invoice_id ? ` · QB ${p.doc_number ?? p.qb_invoice_id}` : ''}${p.voided ? ` · ${t('billing.detail.voidedInQb', 'voided in QuickBooks')}` : ''}`
      : label(p.status);
    if (p.status === 'failed' && p.attempts) {
      return `${base} · ${p.retry_exhausted
        ? t('billing.qbRetriesExhausted', 'automatic retries used up ({{n}})', { n: p.attempts })
        : t('billing.qbRetrying', 'retry {{n}} scheduled', { n: p.attempts })}`;
    }
    return base;
  };
  const error = q.payments?.error || q.invoice?.error;
  return (
    <div className="space-y-2">
      <p className="eyebrow">{t('billing.detail.quickbooks', 'QuickBooks')}</p>
      {rec && (
        <div className="rounded-control border border-warning/40 bg-warning/10 px-3 py-2 text-warning-text">
          <p className="eyebrow">{t('billing.detail.reconciliation', 'Reconciliation required')}</p>
          <p className="mt-1">{rec.detail}</p>
          {(rec.also ?? []).map((x) => <p key={x} className="mt-0.5">{x}</p>)}
          <p className="mt-1 text-ui-text3">
            {t('billing.detail.reconciliationNote', 'Detected {{date}}. Smart Contractor was not changed, and automatic sync of this invoice is paused until someone resolves it.', { date: day(rec.detected_at) })}
          </p>
        </div>
      )}
      {q.unlinked && <p className="rounded-control bg-ui-subtle px-3 py-2 text-ui-text2">{t('billing.detail.unlinked', 'Unlinked from QuickBooks — a QuickBooks-side change was accepted, so this invoice no longer syncs.')}</p>}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
        <dt className="text-ui-text3">{t('billing.detail.qbInvoiceSync', 'Invoice sync')}</dt>
        <dd className={tone(q.invoice?.status)}>{part(q.invoice)}</dd>
        <dt className="text-ui-text3">{t('billing.detail.qbPaymentSync', 'Payment sync')}</dt>
        <dd className={tone(q.payments?.status)}>{part(q.payments)}</dd>
        {error && (
          <>
            <dt className="text-ui-text3">{t('billing.detail.cause', 'Cause')}</dt>
            <dd className="text-ui-text">{error}</dd>
          </>
        )}
        {(q.invoice?.last_sync || q.payments?.last_sync) && (
          <>
            <dt className="text-ui-text3">{t('billing.detail.lastSync', 'Last sync')}</dt>
            <dd className="text-ui-text">{day(q.payments?.last_sync || q.invoice?.last_sync)}</dd>
          </>
        )}
      </dl>
      {(q.activity?.length ?? 0) > 0 && (
        <div>
          <p className="text-ui-text3">{t('billing.detail.postSettlement', 'Changes seen in QuickBooks (recorded only — nothing here was changed)')}</p>
          <ul className="mt-0.5 space-y-0.5 text-ui-text2">
            {(q.activity ?? []).map((a, i) => <li key={i}>{day(a.at)} · {a.detail}</li>)}
          </ul>
        </div>
      )}
      <p className="text-ui-text3">{t('billing.detail.qbGuard', 'Smart Contractor stays the record of truth; a retry never creates a second QuickBooks invoice or payment.')}</p>
    </div>
  );
};

const MAIN_PATH: InvoiceStatus[] = ['Prepared', 'Approved', 'Sent', 'Partially Paid', 'Paid'];

/** The mockup's INVOICE LIFECYCLE strip: the five main-path steps (done / current / ahead) and the two
 *  off-path states, lit only when the invoice is actually there. */
const LifecycleStrip: React.FC<{ status: InvoiceStatus; hadReturn: boolean }> = ({ status, hadReturn }) => {
  const { t } = useTranslation();
  // A Returned invoice is waiting to be resubmitted, i.e. back at the first step.
  const at = status === 'Returned' || status === 'Cancelled' ? -1 : MAIN_PATH.indexOf(status);
  return (
    <div className="space-y-1.5">
      <p className="eyebrow">{t('billing.detail.lifecycle', 'Invoice lifecycle')}</p>
      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
        {MAIN_PATH.map((s, i) => {
          const state = i < at ? 'past' : i === at ? 'now' : 'ahead';
          return (
            <span
              key={s}
              aria-current={state === 'now' ? 'step' : undefined}
              className={`flex-none whitespace-nowrap rounded-full border px-2.5 py-0.5 text-micro font-semibold ${
                state === 'now' ? 'border-accent bg-accent-fill text-on-accent'
                  : state === 'past' ? 'border-success/40 bg-success/15 text-success-text'
                    : 'border-ui-border text-ui-text3'}`}
            >
              {state === 'past' ? '✓ ' : ''}{t(`billing.status.${s}`, s)}
            </span>
          );
        })}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-micro text-ui-text3">{t('billing.detail.offPath', 'Off-path')}</span>
        <span className={`rounded-full border px-2.5 py-0.5 text-micro font-semibold ${
          status === 'Returned' ? 'border-warning/40 bg-warning/15 text-warning-text' : 'border-ui-border text-ui-text3'}`}
        >
          {t('billing.status.Returned', 'Returned')}{hadReturn && status !== 'Returned' ? ' ·' : ''}
        </span>
        <span className={`rounded-full border px-2.5 py-0.5 text-micro font-semibold ${
          status === 'Cancelled' ? 'border-danger/40 bg-danger/15 text-danger-text' : 'border-ui-border text-ui-text3'}`}
        >
          {t('billing.status.Cancelled', 'Cancelled')}
        </span>
      </div>
    </div>
  );
};

const PAYMENT_METHODS = ['ACH transfer', 'Check', 'Card', 'Cash', 'Other'];

const InvoiceDetail: React.FC<{ inv: Invoice }> = ({ inv }) => {
  const { t } = useTranslation();
  const currentUserId = useAppSelector((s) => s.sessionState.userId);
  if (inv.restricted) {
    return <p className="px-3.5 py-3 text-caption text-ui-text3">{t('billing.detail.restricted', 'Amounts, lines and payments are visible to Owner and Office Admin only.')}</p>;
  }
  return (
    <div className="space-y-3 px-3.5 py-3 text-caption">
      <LifecycleStrip status={inv.status} hadReturn={(inv.versions?.length ?? 0) > 0} />
      {inv.status === 'Returned' && inv.return_reason && (
        <div className="rounded-control border border-warning/40 bg-warning/10 px-3 py-2 text-warning-text">
          <p className="eyebrow">{t('billing.detail.returnReason', 'Return reason')}</p>
          <p className="mt-1">“{inv.return_reason}” — {inv.returned_by}, {day(inv.returned_at)}</p>
        </div>
      )}
      {inv.status === 'Cancelled' && inv.cancel_reason && (
        <div className="rounded-control border border-danger/40 bg-danger/10 px-3 py-2 text-danger-text">
          <p className="eyebrow">{t('billing.detail.cancelReason', 'Cancellation reason')}</p>
          <p className="mt-1">“{inv.cancel_reason}” — {inv.cancelled_by}. {t('billing.detail.retained', 'Retained for audit; excluded from every total.')}</p>
        </div>
      )}
      {inv.validation && inv.validation.outcome !== 'not_checked' && inv.validation.accepted_value != null && (
        <div className={`rounded-control border px-3 py-2 ${inv.validation.within_value ? 'border-ui-border' : 'border-warning/40 bg-warning/10 text-warning-text'}`}>
          <p className="eyebrow">{t('billing.detail.cumulative', 'Cumulative validation')}</p>
          <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1">
            <dt className="text-ui-text3">{t('billing.detail.acceptedValue', 'Accepted commercial value')}</dt><dd className="text-ui-text">{money(inv.validation.accepted_value)}</dd>
            <dt className="text-ui-text3">{t('billing.detail.previouslyInvoiced', 'Previously invoiced (active)')}</dt><dd className="text-ui-text">{money(inv.validation.previously_invoiced)}</dd>
            <dt className="text-ui-text3">{t('billing.detail.thisInvoice', 'This invoice')}</dt><dd className="text-ui-text">{money(inv.validation.this_invoice)}</dd>
            <dt className="text-ui-text3">{t('billing.detail.cumulativeTotal', 'Cumulative')}</dt>
            <dd className="text-ui-text">
              {money(inv.validation.cumulative)} {inv.validation.within_value
                ? t('billing.detail.withinValue', '· within value')
                : t('billing.detail.overValue', '· {{amount}} over the accepted value (warned, submission allowed)', { amount: money(inv.validation.over_by) })}
            </dd>
          </dl>
        </div>
      )}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        <dt className="text-ui-text3">{t('billing.detail.version', 'Version')}</dt><dd className="text-ui-text">v{inv.version ?? 1}</dd>
        <dt className="text-ui-text3">{t('billing.detail.invoiceAmount', 'Invoice amount')}</dt><dd className="text-ui-text">{money(inv.amount)}</dd>
        <dt className="text-ui-text3">{t('billing.detail.paidToDate', 'Paid to date')}</dt><dd className="text-ui-text">{money(inv.paid)}</dd>
        <dt className="text-ui-text3">{t('billing.detail.outstanding', 'Outstanding')}</dt><dd className="text-ui-text">{money(inv.balance)}</dd>
        <dt className="text-ui-text3">{t('billing.detail.terms', 'Terms')}</dt><dd className="text-ui-text">{inv.payment_terms} · {t('billing.detail.due', 'due')} {day(inv.due_date)}</dd>
        {inv.approved_by && (<><dt className="text-ui-text3">{t('billing.detail.approvedBy', 'Approved by')}</dt><dd className="text-ui-text">{inv.approved_by} · {day(inv.approved_at)}</dd></>)}
        {inv.sent_by && (<><dt className="text-ui-text3">{t('billing.detail.sentBy', 'Sent by')}</dt><dd className="text-ui-text">{inv.sent_by} · {day(inv.sent_at)}</dd></>)}
      </dl>
      {inv.is_overdue && (
        <p className="rounded-control bg-warning/10 px-3 py-2 text-warning-text">
          {t('billing.detail.overdue', 'Overdue {{count}} day(s) — status is unchanged; overdue clears when the balance reaches zero.', { count: inv.overdue_days })}
        </p>
      )}
      {(inv.payments?.length ?? 0) > 0 && (
        <div>
          <p className="eyebrow mb-1">{t('billing.detail.payments', 'Payments on this invoice')}</p>
          <ul className="divide-y divide-ui-border rounded-control border border-ui-border">
            {inv.payments.map((p, i) => (
              <li key={i} className="flex items-center justify-between gap-3 px-3 py-1.5">
                <span className={p.reversed ? 'text-ui-text3 line-through' : 'text-ui-text2'}>{day(p.date)} · {p.method}{p.reference ? ` · ${t('billing.detail.ref', 'ref')} ${p.reference}` : ''}{p.reversed ? ` · ${t('billing.detail.reversed', 'reversed in QuickBooks')}` : ''}</span>
                <span className={`font-semibold ${p.reversed ? 'text-ui-text3 line-through' : 'text-ui-text'}`}>{money(p.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {inv.quickbooks && <QuickBooksBlock inv={inv} />}
      {(inv.versions?.length ?? 0) > 0 && (
        <div>
          <p className="eyebrow mb-1">{t('billing.detail.versions', 'Versions')}</p>
          <ul className="space-y-1 text-ui-text2">
            {(inv.versions ?? []).map((v) => (
              <li key={v.version}>
                v{v.version} · {t('billing.detail.frozen', 'frozen')} {day(v.frozen_at)} · {v.frozen_by}{v.amount != null ? ` · ${money(v.amount)}` : ''}
              </li>
            ))}
            <li>v{inv.version ?? 1} · {inv.locked_at ? t('billing.detail.locked', 'approved · locked') : t('billing.detail.working', 'working version')}</li>
          </ul>
        </div>
      )}
      {(inv.reminders?.length ?? 0) > 0 && (
        <div>
          <p className="eyebrow mb-1">{t('billing.detail.reminders', 'Reminders sent')}</p>
          <ul className="space-y-1 text-ui-text2">
            {(inv.reminders ?? []).map((r, i) => (
              <li key={i}>{day(r.at)} · {r.channel} · {r.recipient} · {t('billing.detail.daysOverdue', '{{count}} day(s) overdue', { count: r.days_overdue })}</li>
            ))}
          </ul>
        </div>
      )}
      {(inv.history?.length ?? 0) > 0 && (
        <div>
          <p className="eyebrow mb-1">{t('billing.detail.history', 'History')}</p>
          <ul className="space-y-1 text-ui-text2">
            {[...(inv.history ?? [])].reverse().map((h, i) => (
              <li key={i}>
                {day(h.at)} · <span title={h.by}>{actorLabel(h.by, h.by_user_id, currentUserId, t)}</span> — {h.event}
                {h.reason ? ` (“${h.reason}”)` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const InvoiceLifecycleList: React.FC<{ engagementId: string; invoices: Invoice[] }> = ({ engagementId, invoices }) => {
  const { t } = useTranslation();
  const { data: perms } = useGetInvoicePermissionsQuery(engagementId);
  const [runAction, { isLoading }] = useInvoiceActionMutation();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pending, setPending] = useState<{ inv: Invoice; act: RowAction } | null>(null);
  const [reason, setReason] = useState('');
  const [resolution, setResolution] = useState<'resync' | 'accept_quickbooks_change'>('resync');
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState(PAYMENT_METHODS[0]);
  const [payRef, setPayRef] = useState('');
  const [payDate, setPayDate] = useState(() => localDate());
  const [error, setError] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());

  // SAM's get_invoice / invoice writes (OPEN_ENGAGEMENT_INVOICE): unfold that invoice and bring it into view.
  useEffect(() => subscribeEngagementSignal('open_invoice', engagementId, (invoiceId) => {
    if (!invoiceId) return;
    setExpanded(invoiceId);
    setTimeout(() => rowRefs.current.get(invoiceId)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 250);
  }), [engagementId]);

  const can: Record<string, boolean> = perms ?? {};

  const run = async (inv: Invoice, act: RowAction, extra: Record<string, unknown> = {}) => {
    setError(null);
    try {
      await runAction({ engagementId, invoiceId: inv.invoice_id, action: act.code, ...extra }).unwrap();
      setPending(null);
      setReason('');
      setPayAmount('');
      setPayRef('');
    } catch (e) {
      const detail = (e as { data?: { detail?: string } })?.data?.detail;
      setError(detail ?? t('billing.error.generic', 'That did not go through. Please try again.'));
    }
  };

  const start = (inv: Invoice, act: RowAction) => {
    setError(null);
    if (act.prompt === 'none') { void run(inv, act); return; }
    if (act.prompt === 'payment') setPayAmount(String(inv.balance ?? ''));
    setPending({ inv, act });
  };

  const shortDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div className="border-t border-ui-border">
      {error && !pending && <p className="border-b border-ui-border bg-danger/10 px-3.5 py-2 text-caption text-danger-text">{error}</p>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left">
          <thead>
            <tr className="bg-ui-subtle">
              {[
                t('billing.col.date', 'Date'), t('billing.col.invoice', 'Invoice · QuickBooks'), t('billing.col.amount', 'Amount'),
                t('billing.col.paid', 'Paid'), t('billing.col.status', 'Status'), t('billing.col.action', 'Action'),
              ].map((h, i) => (
                <th key={h} className={`px-3 py-2 eyebrow ${i >= 2 && i <= 3 ? 'text-right' : ''} ${i === 5 ? 'text-right' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-ui-border">
            {invoices.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3.5 py-5 text-center text-caption text-ui-text3">
                  {t('billing.empty', 'No invoices raised on this engagement yet. Create one whenever it is required — the engagement status does not change.')}
                </td>
              </tr>
            )}
            {invoices.map((inv) => {
              const acts = inv.restricted ? [] : actionsFor(inv, can, t);
              const open = expanded === inv.invoice_id;
              return (
                <React.Fragment key={inv.invoice_id}>
                  <tr
                    className="align-top"
                    ref={(el) => { if (el) rowRefs.current.set(inv.invoice_id, el); else rowRefs.current.delete(inv.invoice_id); }}
                  >
                    <td className="whitespace-nowrap px-3 py-3 text-caption text-ui-text2">{shortDate(inv.created_at)}</td>
                    <td className="px-3 py-3">
                      <p className="text-body font-semibold text-accent-text">
                        {inv.number}{(inv.version ?? 1) > 1 ? <span className="ml-1 text-caption font-normal text-ui-text3">v{inv.version}</span> : null}
                      </p>
                      <p className={`text-caption ${inv.quickbooks_summary?.issue ? 'text-warning-text' : 'text-ui-text3'}`}>
                        {inv.quickbooks_summary?.issue
                          ? `QB · ${t(`billing.qbStatus.${inv.quickbooks_summary.status}`, inv.quickbooks_summary.status)}`
                          : inv.quickbooks_ref ? `QB · ${inv.quickbooks_ref}` : t('billing.qbNotSynced', 'QB · Not synced')}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right text-body font-semibold text-ui-text">
                      {inv.restricted ? <span className="text-caption text-ui-text2">{t('billing.restricted', 'Restricted')}</span> : money(inv.amount)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right text-caption text-ui-text2">
                      {inv.restricted ? t('billing.restricted', 'Restricted') : money(inv.paid)}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <Badge variant={STATUS_TONE[inv.status] ?? 'neutral'}>{t(`billing.status.${inv.status}`, inv.status)}</Badge>
                        {inv.is_overdue && <Badge variant="warning">{t('billing.overdueBadge', 'Overdue · {{count}}d', { count: inv.overdue_days })}</Badge>}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        {acts.map((a) => (
                          <Button key={a.code} type="button" size="xs" variant={a.variant} disabled={isLoading} onClick={() => start(inv, a)}>
                            {a.label}
                          </Button>
                        ))}
                        <Button type="button" size="xs" variant="outline" onClick={() => setExpanded(open ? null : inv.invoice_id)}>
                          {open ? t('billing.hide', 'Hide') : t('billing.view', 'View')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={6} className="bg-ui-subtle/50 p-0"><InvoiceDetail inv={inv} /></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {pending && pending.act.prompt === 'reason' && (
        <Modal
          title={`${pending.act.label} ${pending.inv.number}`}
          onClose={() => { setPending(null); setReason(''); setError(null); }}
          onSave={() => void run(pending.inv, pending.act, { reason: reason.trim() })}
          saveLabel={pending.act.label}
          saveDisabled={!reason.trim()}
          saving={isLoading}
          danger={pending.act.code === 'cancel'}
        >
          <p className="text-body text-ui-text2">
            {pending.act.code === 'return'
              ? t('billing.confirm.return', 'The preparer is told why and revises the invoice; the returned version is frozen for audit.')
              : t('billing.confirm.cancel', 'A cancelled invoice is kept for audit but excluded from every total. It cannot be cancelled once a payment is recorded, and this cannot be undone.')}
          </p>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('billing.reasonPlaceholder', 'Reason (required)')} rows={3} />
          {error && <p className="text-caption text-danger-text">{error}</p>}
        </Modal>
      )}

      {pending && pending.act.prompt === 'resolve' && (
        <Modal
          title={`${pending.act.label} — ${pending.inv.number}`}
          onClose={() => { setPending(null); setReason(''); setError(null); }}
          onSave={() => void run(pending.inv, pending.act, { resolution, reason: reason.trim() })}
          saveLabel={t('billing.action.resolve', 'Resolve')}
          saving={isLoading}
        >
          <p className="text-body text-ui-text2">{pending.inv.quickbooks?.reconciliation?.detail}</p>
          <p className="text-caption text-ui-text3">{t('billing.resolve.intro', 'Nothing in Smart Contractor changes when you resolve this — the invoice and its payments stay exactly as they are.')}</p>
          <label className="block space-y-1 text-caption text-ui-text3">{t('billing.resolve.how', 'How should it be settled?')}
            <Select value={resolution} onChange={(e) => setResolution(e.target.value as 'resync' | 'accept_quickbooks_change')}>
              <option value="resync">{t('billing.resolve.resync', 'Keep Smart Contractor as it is and resume syncing')}</option>
              <option value="accept_quickbooks_change">{t('billing.resolve.accept', 'Accept the QuickBooks change and stop syncing this invoice')}</option>
            </Select>
          </label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('billing.resolve.note', 'Note (optional) — what was checked or changed')} rows={2} />
          {error && <p className="text-caption text-danger-text">{error}</p>}
        </Modal>
      )}

      {pending && pending.act.prompt === 'payment' && (
        <Modal
          title={`${t('billing.action.record_payment', 'Record payment')} — ${pending.inv.number}`}
          onClose={() => { setPending(null); setError(null); }}
          onSave={() => void run(pending.inv, pending.act, {
            payment_amount: Number(payAmount), payment_method: payMethod,
            payment_reference: payRef.trim() || undefined, payment_date: payDate,
          })}
          saveLabel={t('billing.action.record_payment', 'Record payment')}
          saveDisabled={!(Number(payAmount) > 0)}
          saving={isLoading}
        >
          <p className="text-body text-ui-text2">{t('billing.payment.balance', 'Outstanding balance')} {money(pending.inv.balance)}.</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1 text-caption text-ui-text3">{t('billing.payment.amount', 'Amount')}
              <Input type="number" min="0" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
            </label>
            <label className="space-y-1 text-caption text-ui-text3">{t('billing.payment.date', 'Date')}
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
            </label>
            <label className="space-y-1 text-caption text-ui-text3">{t('billing.payment.method', 'Method')}
              <Select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{t(`billing.method.${m}`, m)}</option>)}
              </Select>
            </label>
            <label className="space-y-1 text-caption text-ui-text3">{t('billing.payment.reference', 'Reference')}
              <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder={t('billing.payment.referencePlaceholder', 'Check # / ACH ref')} />
            </label>
          </div>
          {error && <p className="text-caption text-danger-text">{error}</p>}
        </Modal>
      )}
    </div>
  );
};

/** Whether the viewer's role may prepare invoices (drives the Create button vs. the notice). */
export const useCanPrepareInvoice = (engagementId: string) => {
  const { data } = useGetInvoicePermissionsQuery(engagementId, { skip: !engagementId });
  return { canPrepare: !!data?.invoice_prepare, canSeeAmounts: !!data?.invoice_view_amounts, loaded: !!data };
};
